/**
 * Django session auth + CSRF (no csrf_exempt assumptions).
 *
 * CRITICAL: Cross-origin `fetch` defaults to credentials: "omit". Without credentials: "include"
 * on EVERY GET /auth/csrf/ and every CSRF-protected POST, the browser will not store Set-Cookie
 * for the API origin → Django returns "CSRF cookie not set" even when X-CSRFToken is sent.
 *
 * Flow:
 * - Public host: POST discover-tenants (csrf_exempt — no CSRF round-trip).
 * - Tenant host: GET csrf → POST login/logout with X-CSRFToken; GET /me/ — all via apiFetch() (credentials: 'include').
 * After successful login, tenant origin is persisted for /me/ and logout (session is not on the public host).
 *
 * When the SPA and API differ by hostname (e.g. SPA on localhost:3000, API on {slug}.localhost:8000),
 * POST /api/v1/auth/login/ may return session_pending. In that case the backend may expose the
 * first-party bootstrap hop via redirect_url, falling back to session_bootstrap_url. You MUST
 * full-page navigate before using GET /me/ — otherwise sessionid is not set for the API origin.
 */

import { POST_LOGIN_TARGET, PUBLIC_API_URL, SESSION_API_BASE_URL } from '@/config/api';
import {
  clearStoredTenantRedirectContext,
  getLocalDevTenantApiOrigin,
  getTenantSlug,
  rewriteDevLocalhostUrl,
  rewriteUrlToTenantOrigin,
  setStoredTenantRedirectContext,
} from '@/lib/postLoginRedirect';

/** Persisted after successful tenant login — session + CSRF cookies are host-scoped. */
const TENANT_API_BASE_STORAGE_KEY = 'carnitrack.tenantApiBaseUrl';

function isAuthRedirectDebugEnabled() {
  if (typeof window === 'undefined') return false;

  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get('debugAuth') === '1') return true;
    return window.localStorage.getItem('carnitrack.debugAuthRedirect') === '1';
  } catch {
    return false;
  }
}

function pickTenantRedirectFields(tenant) {
  if (!tenant || typeof tenant !== 'object') return null;

  return {
    slug: getTenantSlug(tenant),
    primary_domain: tenant.primary_domain || '',
    web_app_base_url: tenant.web_app_base_url || '',
    post_login_redirect_url: tenant.post_login_redirect_url || '',
    api_base_url: tenant.api_base_url || '',
    auth_login_url: tenant.auth_login_url || '',
  };
}

export function logAuthRedirectDebug(label, data) {
  if (!isAuthRedirectDebugEnabled()) return;
  console.info(`[CarniTrack][auth] ${label}`, data);
}

/**
 * Cross-origin fetch defaults to credentials: "omit". Session + CSRF cookies are only
 * stored when credentials: "include" is set — required on every tenant call:
 * GET /csrf/, POST /login/, GET /me/, POST /logout/ (and discover POST on the public host).
 */
function apiFetch(input, init = {}) {
  return fetch(input, { ...init, credentials: 'include' });
}

/** Normalize email for discover (trim + lowercase). */
export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Strip formatting and return a leading-+ normalized phone string. */
export function normalizePhone(phone, areaCode = '+90') {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (raw.startsWith('+')) return `+${digits}`;
  const areaDigits = String(areaCode || '').replace(/\D/g, '');
  return `+${areaDigits}${digits}`;
}

/** Strip trailing slash from API origin. */
export function normalizeOrigin(url) {
  return String(url || '').replace(/\/$/, '');
}

export function getStoredTenantApiBaseUrl() {
  const localDevOrigin = normalizeOrigin(getLocalDevTenantApiOrigin());
  if (localDevOrigin) return localDevOrigin;

  try {
    const v = localStorage.getItem(TENANT_API_BASE_STORAGE_KEY);
    return v ? normalizeOrigin(rewriteDevLocalhostUrl(v)) : '';
  } catch {
    return '';
  }
}

export function setStoredTenantApiBaseUrl(url) {
  const localDevOrigin = normalizeOrigin(getLocalDevTenantApiOrigin());
  const n = localDevOrigin || normalizeOrigin(rewriteDevLocalhostUrl(url));
  if (!n) return;
  try {
    localStorage.setItem(TENANT_API_BASE_STORAGE_KEY, n);
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearStoredTenantApiBaseUrl() {
  try {
    localStorage.removeItem(TENANT_API_BASE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : null;

  if (!response.ok) {
    const message =
      data?.detail ||
      data?.message ||
      (response.status === 403 ? 'CSRF verification failed' : null) ||
      'Request failed';
    throw new Error(message);
  }

  return data;
}

/**
 * GET CSRF JSON + cookie for this API origin.
 * @param {string} baseUrl - Origin only, e.g. https://api.example.com or http://dev.localhost:8000
 */
export async function getCsrfToken(baseUrl) {
  const origin = normalizeOrigin(baseUrl);
  const response = await apiFetch(`${origin}/api/v1/auth/csrf/`, {
    method: 'GET',
  });

  const data = await parseResponse(response);
  return data?.csrfToken || '';
}

/** Normalize discover API body to a tenant option array. */
export function normalizeDiscoverResponse(data) {
  if (Array.isArray(data)) return data;
  if (data?.tenants && Array.isArray(data.tenants)) return data.tenants;
  if (data?.results && Array.isArray(data.results)) return data.results;
  return [];
}

/**
 * Discover tenant options (public host only — no password).
 * POST /api/v1/auth/discover-tenants/ (csrf_exempt — no CSRF preflight on this host).
 */
export async function discoverTenants({ email = '', phone = '', phoneAreaCode = '+90' } = {}) {
  const origin = normalizeOrigin(PUBLIC_API_URL);
  const payload = {};
  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phone, phoneAreaCode);
  if (normalizedEmail) payload.email = normalizedEmail;
  if (normalizedPhone) payload.phone = normalizedPhone;

  const response = await apiFetch(`${origin}/api/v1/auth/discover-tenants/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await parseResponse(response);
  return normalizeDiscoverResponse(data);
}

/**
 * Resolve tenant login POST URL from API fields.
 * Prefer auth_login_url; else api_base_url + /api/v1/auth/login/
 */
export function resolveTenantLoginUrl(tenant) {
  if (!tenant || typeof tenant !== 'object') {
    throw new Error('Invalid tenant');
  }
  const localDevOrigin = normalizeOrigin(getLocalDevTenantApiOrigin());
  if (localDevOrigin) {
    return `${localDevOrigin}/api/v1/auth/login/`;
  }
  if (tenant.auth_login_url) {
    return rewriteDevLocalhostUrl(String(tenant.auth_login_url).trim());
  }
  const base = rewriteDevLocalhostUrl(String(tenant.api_base_url || '').replace(/\/$/, ''));
  if (!base) {
    throw new Error('Tenant has no auth_login_url or api_base_url');
  }
  return `${base}/api/v1/auth/login/`;
}

/**
 * Tenant API origin from tenant row (for CSRF + session).
 */
export function resolveTenantApiOrigin(tenant) {
  const localDevOrigin = normalizeOrigin(getLocalDevTenantApiOrigin());
  if (localDevOrigin) {
    return localDevOrigin;
  }

  const loginUrl = resolveTenantLoginUrl(tenant);
  const u = new URL(loginUrl);
  return `${u.protocol}//${u.host}`;
}

/**
 * Suggested SPA origin in dev: same host as `web_app_base_url`, port 3000 (bundler).
 * discover-tenants exposes `web_app_base_url`; the SPA should run there, not on bare localhost.
 * @param {object} tenant - Row from discover-tenants
 * @returns {string}
 */
export function suggestedSpaOriginFromDiscoverTenant(tenant) {
  const raw = tenant?.web_app_base_url;
  if (!raw) return '';
  try {
    const u = new URL(String(raw).trim());
    u.port = '3000';
    return u.origin;
  } catch {
    return '';
  }
}

/**
 * @param {unknown} loginJson
 * @param {object} tenant
 */
function logDevLocalhostCookieHint(loginJson, tenant) {
  if (process.env.NODE_ENV !== 'development' || typeof window === 'undefined') return;
  const { hostname } = window.location;
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') return;

  const suggested = suggestedSpaOriginFromDiscoverTenant(tenant);
  const sameSite = loginJson?.debug?.cookie_same_site;

  const lines = [
    `[CarniTrack] Dev: you opened ${window.location.origin} (localhost / 127.0.0.1). Tenant auth calls are forced to ${getLocalDevTenantApiOrigin() || 'your configured local API origin'} in this mode. If cookies or redirects still fail, switch to the tenant hostname on port 3000 (e.g. http://pomet.localhost:3000), add that origin to Django CORS_ALLOWED_ORIGINS and CSRF_TRUSTED_ORIGINS, and set WDS_SOCKET_HOST / WDS_SOCKET_PORT in .env.development.local (see README).`,
  ];
  if (suggested) {
    lines.push(`Align with discover-tenants web_app_base_url → try opening: ${suggested}`);
  }
  if (sameSite != null) {
    lines.push(`Login JSON debug.cookie_same_site: ${JSON.stringify(sameSite)}`);
  }
  console.warn(lines.join('\n'));
}

/**
 * Call after parsing POST /api/v1/auth/login/ JSON. If the backend requires a session bootstrap
 * (cross-origin cookie), prefer session_bootstrap_url and return true. Call before any redirect_url
 * handling or GET /me/.
 *
 * @param {Record<string, unknown>} loginJson
 * @param {object} tenant
 * @returns {boolean} true if the page is navigating away
 */
export function applyPostLoginNavigation(loginJson, tenant) {
  if (loginJson?.session_pending) {
    const rawTarget = loginJson?.session_bootstrap_url || loginJson?.redirect_url;
    const target = rewriteUrlToTenantOrigin(rawTarget, tenant);
    logAuthRedirectDebug('session bootstrap decision', {
      post_login_target: POST_LOGIN_TARGET,
      session_pending: !!loginJson?.session_pending,
      chosen_target: target || '',
      raw_target: rawTarget || '',
      redirect_url: loginJson?.redirect_url || '',
      session_bootstrap_url: loginJson?.session_bootstrap_url || '',
      tenant: pickTenantRedirectFields(tenant),
    });
    if (target) {
      window.location.assign(String(target));
      return true;
    }
  }
  return false;
}

/** True when login JSON indicates bootstrap navigation was applied or should run. */
export function isPostLoginBootstrapPending(loginJson) {
  return !!(loginJson?.session_pending && loginJson?.session_bootstrap_url);
}

/**
 * Login on tenant host only (password never sent to public API).
 */
export async function loginOnTenant(tenant, username, password) {
  const loginUrl = resolveTenantLoginUrl(tenant);
  const urlObj = new URL(loginUrl);
  const origin = `${urlObj.protocol}//${urlObj.host}`;

  const csrfToken = await getCsrfToken(origin);

  const response = await apiFetch(loginUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: JSON.stringify({ username, password, post_login_redirect: POST_LOGIN_TARGET }),
  });

  const data = await parseResponse(response);
  logAuthRedirectDebug('login response', {
    post_login_target: POST_LOGIN_TARGET,
    session_pending: !!data?.session_pending,
    redirect_url: data?.redirect_url || '',
    session_bootstrap_url: data?.session_bootstrap_url || '',
    tenant: pickTenantRedirectFields(tenant),
  });
  setStoredTenantApiBaseUrl(resolveTenantApiOrigin(tenant));
  setStoredTenantRedirectContext(tenant);
  logDevLocalhostCookieHint(data, tenant);
  applyPostLoginNavigation(data, tenant);
  return data;
}

/**
 * Current user — must use the same tenant origin as login (persisted `tenantApiBaseUrl`).
 * Falls back to SESSION_API_BASE_URL when unset (single-origin / env-configured builds).
 */
export async function getCurrentUser() {
  const stored = getStoredTenantApiBaseUrl();
  const base = normalizeOrigin(stored || SESSION_API_BASE_URL);
  const response = await apiFetch(`${base}/api/v1/auth/me/`, {
    method: 'GET',
  });

  if (response.status === 401) {
    clearStoredTenantApiBaseUrl();
    clearStoredTenantRedirectContext();
  }

  return parseResponse(response);
}

/**
 * Logout (CSRF required on same origin as session — tenant host after email-first login).
 */
export async function logout() {
  const stored = getStoredTenantApiBaseUrl();
  const base = normalizeOrigin(stored || SESSION_API_BASE_URL);
  const csrfToken = await getCsrfToken(base);

  try {
    const response = await apiFetch(`${base}/api/v1/auth/logout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
    });

    return await parseResponse(response);
  } finally {
    clearStoredTenantApiBaseUrl();
    clearStoredTenantRedirectContext();
  }
}
